/**
 * Where a back arrow goes.
 *
 * The arrow means "the screen I came from", not "the parent page in the site
 * map" — so it walks the router history whenever there is one to walk. A direct
 * load (a shared link, a new tab, a bookmark, a redirect from outside) has no
 * previous screen inside the app: Vue Router leaves `history.state.back` null,
 * and the arrow falls back to the route the page declares so it never dead-ends
 * on a blank history entry nor throws the user out of the app.
 *
 * Kept pure so the decision can be tested without a browser; the router calls
 * live in `useGoBack`.
 *
 * @param {?{back?: ?string}} historyState `window.history.state`
 * @param {?(Object|string)} fallback Route to land on when there is no history
 * @return {{action: 'back'|'replace'|'none', to?: (Object|string)}}
 */
export function resolveBack(historyState, fallback = null) {
    if (historyState && historyState.back !== null && historyState.back !== undefined) {
        return { action: 'back' };
    }

    if (fallback) {
        return { action: 'replace', to: fallback };
    }

    return { action: 'none' };
}

/**
 * Where the back arrow of a record goes: out of the record, to the screen it
 * was entered from.
 *
 * A record is several screens — its tabs — and each tab clicked is a history
 * entry. Walking back one entry from « Tâches » lands on « Plan », which is
 * not where anybody meant to go: the arrow on a record means « leave it ». So
 * the record notes the history position it was entered at (`entry`), and the
 * arrow goes back past every tab visited since, to whatever came before — the
 * list, a search result, a dashboard, another module's record.
 *
 * Without a usable entry — the record was opened cold, in a new tab or from an
 * email, so nothing precedes it — it falls back exactly like resolveBack().
 *
 * @param {?{position?: number, back?: ?string}} historyState `window.history.state`
 * @param {?{position: number, hadBack: boolean}} entry where the record was entered
 * @param {?(Object|string)} fallback Route to land on when there is nothing to go back to
 * @return {{action: 'go'|'back'|'replace'|'none', delta?: number, to?: (Object|string)}}
 */
export function resolveLeave(historyState, entry, fallback = null) {
    const current = historyState?.position;

    if (entry?.hadBack && Number.isInteger(entry.position) && Number.isInteger(current) && entry.position <= current) {
        return { action: 'go', delta: entry.position - 1 - current };
    }

    if (entry && !entry.hadBack) {
        return fallback ? { action: 'replace', to: fallback } : { action: 'none' };
    }

    return resolveBack(historyState, fallback);
}
